import { calculateEdrDifference, calculateLineupAverageEdr, formatEdrDifference } from './edr.ts';

type RatingBasis = 'avg_edr' | 'subjective' | string | null | undefined;
type PlayerRatingBasis = 'edr' | 'tier' | string | null | undefined;

export type PlanningBoardEdrMatch = {
  id: string;
  team_id?: string | null;
  league_configuration_id: string | null;
  opponent_average_edr?: number | string | null;
  opponent_rating_basis?: RatingBasis;
  opponent_subjective_difficulty?: number | string | null;
};

export type PlanningBoardEdrPosition = {
  id: string;
  league_configuration_id: string | null;
  position_code?: string | null;
  display_order?: number | string | null;
  required_player_count?: number | string | null;
};

export type PlanningBoardEdrEntry = {
  match_event_id: string;
  league_configuration_position_id: string;
  player_id: string;
};

export type PlanningBoardEdrPlayer = {
  id: string;
  estimated_dynamic_rating?: number | string | null;
  player_tier?: string | number | null;
};

export type PlanningBoardCompetitivenessTeam = {
  id: string;
  player_rating_basis?: PlayerRatingBasis;
};

export type PlanningBoardEdrIndicator =
  | {
      show: false;
      label: null;
      tooltip: null;
      tone: 'neutral';
    }
  | {
      show: true;
      label: string;
      tooltip: string;
      tone: 'positive' | 'negative' | 'neutral' | 'unavailable';
      difference: number | null;
      rating?: 'Strong' | 'Balanced' | 'Challenging';
    };

const omittedIndicator: PlanningBoardEdrIndicator = {
  show: false,
  label: null,
  tooltip: null,
  tone: 'neutral',
};

export const planningBoardMatchupThresholds = {
  strong: 0.5,
  challenging: -0.5,
} as const;

const defaultedLineStatuses = new Set(['default_by_opponent', 'default_by_us']);

function numericValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function requiredPlayerCount(position: PlanningBoardEdrPosition): number {
  const parsed = Number(position.required_player_count);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function displayOrder(position: PlanningBoardEdrPosition): number {
  const parsed = numericValue(position.display_order);
  return parsed === null ? Number.MAX_SAFE_INTEGER : parsed;
}

function positionCode(position: PlanningBoardEdrPosition): string {
  return position.position_code || 'Line';
}

function edrTone(difference: number): PlanningBoardEdrIndicator['tone'] {
  if (difference > 0) return 'positive';
  if (difference < 0) return 'negative';
  return 'neutral';
}

function displayDifference(difference: number): number {
  const rounded = Number(difference.toFixed(2));
  return Object.is(rounded, -0) ? 0 : rounded;
}

export function isPlanningBoardOpponentEdrApplicable(match: PlanningBoardEdrMatch): boolean {
  if (match.opponent_rating_basis === 'subjective') return false;
  return match.opponent_rating_basis === 'avg_edr' || numericValue(match.opponent_average_edr) !== null;
}

function isPlanningBoardTierMatchupApplicable(match: PlanningBoardEdrMatch, team: PlanningBoardCompetitivenessTeam | null | undefined): boolean {
  return team?.player_rating_basis === 'tier' && match.opponent_rating_basis === 'subjective';
}

function subjectiveDifficultyValue(value: unknown): number | null {
  const label = String(value ?? '').trim().toLowerCase();
  if (label === 'easy') return 1;
  if (label === 'even') return 2;
  if (label === 'tough') return 3;
  const numeric = numericValue(value);
  if (numeric === null) return null;
  if (numeric <= 1) return 1;
  if (numeric <= 3) return 2;
  return 3;
}

function subjectiveDifficultyLabel(value: number): string {
  if (value <= 1) return 'Easy';
  if (value >= 3) return 'Tough';
  return 'Even';
}

function tierStrengthValue(playerTier: unknown): number | null {
  const match = String(playerTier ?? '').trim().toLowerCase().match(/^(?:tier[_\s-]*)?([1-4])$/);
  if (!match) return null;
  const tier = Number(match[1]);
  if (tier === 1) return 3;
  if (tier === 2) return 2;
  if (tier === 3 || tier === 4) return 1;
  return null;
}

function matchupRating(difference: number): 'Strong' | 'Balanced' | 'Challenging' {
  if (difference >= planningBoardMatchupThresholds.strong) return 'Strong';
  if (difference <= planningBoardMatchupThresholds.challenging) return 'Challenging';
  return 'Balanced';
}

function matchupTone(rating: 'Strong' | 'Balanced' | 'Challenging'): PlanningBoardEdrIndicator['tone'] {
  if (rating === 'Strong') return 'positive';
  if (rating === 'Challenging') return 'negative';
  return 'neutral';
}

function assignedPlayersForCompleteLineup({
  match,
  positions,
  entries,
  playersById,
  lineStatusesByPositionId = new Map(),
}: {
  match: PlanningBoardEdrMatch;
  positions: PlanningBoardEdrPosition[];
  entries: PlanningBoardEdrEntry[];
  playersById: Map<string, PlanningBoardEdrPlayer>;
  lineStatusesByPositionId?: Map<string, string | null | undefined>;
}): { complete: true; players: PlanningBoardEdrPlayer[] } | { complete: false; players: PlanningBoardEdrPlayer[] } {
  const playablePositions = positions
    .filter((position) => position.league_configuration_id === match.league_configuration_id)
    .filter((position) => !defaultedLineStatuses.has(String(lineStatusesByPositionId.get(position.id) || 'normal')))
    .sort((a, b) => displayOrder(a) - displayOrder(b) || positionCode(a).localeCompare(positionCode(b), undefined, { numeric: true }));

  if (playablePositions.length === 0) return { complete: false, players: [] };

  const matchEntries = entries.filter((entry) => entry.match_event_id === match.id);
  const assignedLineupPlayers: PlanningBoardEdrPlayer[] = [];
  for (const position of playablePositions) {
    const requiredCount = requiredPlayerCount(position);
    const positionEntries = matchEntries
      .filter((entry) => entry.league_configuration_position_id === position.id)
      .slice(0, requiredCount);
    if (positionEntries.length < requiredCount) return { complete: false, players: assignedLineupPlayers };

    for (const entry of positionEntries) {
      const player = playersById.get(entry.player_id);
      if (!player) return { complete: false, players: assignedLineupPlayers };
      assignedLineupPlayers.push(player);
    }
  }

  return { complete: true, players: assignedLineupPlayers };
}

export function buildPlanningBoardTierMatchupIndicator({
  match,
  team,
  positions,
  entries,
  playersById,
  lineStatusesByPositionId,
}: {
  match: PlanningBoardEdrMatch;
  team: PlanningBoardCompetitivenessTeam | null | undefined;
  positions: PlanningBoardEdrPosition[];
  entries: PlanningBoardEdrEntry[];
  playersById: Map<string, PlanningBoardEdrPlayer>;
  lineStatusesByPositionId?: Map<string, string | null | undefined>;
}): PlanningBoardEdrIndicator {
  if (!isPlanningBoardTierMatchupApplicable(match, team)) return omittedIndicator;

  const lineup = assignedPlayersForCompleteLineup({ match, positions, entries, playersById, lineStatusesByPositionId });
  if (!lineup.complete) {
    return {
      show: true,
      label: 'Matchup: —',
      tooltip: 'Complete the lineup to calculate the matchup rating.',
      tone: 'unavailable',
      difference: null,
    };
  }

  const opponentDifficulty = subjectiveDifficultyValue(match.opponent_subjective_difficulty);
  if (opponentDifficulty === null) {
    return {
      show: true,
      label: 'Matchup: —',
      tooltip: 'Set the opponent difficulty to calculate the matchup rating.',
      tone: 'unavailable',
      difference: null,
    };
  }

  const tierValues = lineup.players.map((player) => tierStrengthValue(player.player_tier));
  if (tierValues.some((value) => value === null)) {
    return {
      show: true,
      label: 'Matchup: —',
      tooltip: 'Matchup rating unavailable because one or more assigned players does not have a player tier.',
      tone: 'unavailable',
      difference: null,
    };
  }

  const numericTierValues = tierValues.filter((value): value is number => value !== null);
  const lineupStrength = numericTierValues.reduce((sum, value) => sum + value, 0) / numericTierValues.length;
  const difference = Number((lineupStrength - opponentDifficulty).toFixed(2));
  const rating = matchupRating(difference);
  const tierDistribution = [
    { label: 'Tier 1', count: numericTierValues.filter((value) => value === 3).length },
    { label: 'Tier 2', count: numericTierValues.filter((value) => value === 2).length },
    { label: 'Tier 3', count: numericTierValues.filter((value) => value === 1).length },
  ].filter((item) => item.count > 0).map((item) => `${item.count} ${item.label}`).join(' · ');

  return {
    show: true,
    label: `Matchup: ${rating}`,
    tooltip: [
      `${rating} Matchup`,
      `Opponent: ${subjectiveDifficultyLabel(opponentDifficulty)}`,
      `Lineup strength: ${lineupStrength.toFixed(2)}`,
      `Based on the tiers of ${numericTierValues.length} assigned players.`,
      tierDistribution,
    ].filter(Boolean).join('\n'),
    tone: matchupTone(rating),
    difference,
    rating,
  };
}

export function buildPlanningBoardCompetitivenessIndicator({
  match,
  team,
  positions,
  entries,
  playersById,
  lineStatusesByPositionId,
}: {
  match: PlanningBoardEdrMatch;
  team: PlanningBoardCompetitivenessTeam | null | undefined;
  positions: PlanningBoardEdrPosition[];
  entries: PlanningBoardEdrEntry[];
  playersById: Map<string, PlanningBoardEdrPlayer>;
  lineStatusesByPositionId?: Map<string, string | null | undefined>;
}): PlanningBoardEdrIndicator {
  if (isPlanningBoardTierMatchupApplicable(match, team)) {
    return buildPlanningBoardTierMatchupIndicator({ match, team, positions, entries, playersById, lineStatusesByPositionId });
  }
  return buildPlanningBoardEdrIndicator({ match, positions, entries, playersById });
}

export function buildPlanningBoardEdrIndicator({
  match,
  positions,
  entries,
  playersById,
}: {
  match: PlanningBoardEdrMatch;
  positions: PlanningBoardEdrPosition[];
  entries: PlanningBoardEdrEntry[];
  playersById: Map<string, PlanningBoardEdrPlayer>;
}): PlanningBoardEdrIndicator {
  if (!isPlanningBoardOpponentEdrApplicable(match)) return omittedIndicator;

  const opponentAverageEdr = numericValue(match.opponent_average_edr);
  if (opponentAverageEdr === null) {
    return {
      show: true,
      label: 'EDR Δ —',
      tooltip: 'Opponent EDR unavailable',
      tone: 'unavailable',
      difference: null,
    };
  }

  const playablePositions = positions.filter((position) => position.league_configuration_id === match.league_configuration_id);
  if (playablePositions.length === 0) {
    return {
      show: true,
      label: 'EDR Δ —',
      tooltip: 'Incomplete lineup',
      tone: 'unavailable',
      difference: null,
    };
  }

  const matchEntries = entries.filter((entry) => entry.match_event_id === match.id);
  const assignedLineupPlayers: PlanningBoardEdrPlayer[] = [];
  for (const position of playablePositions) {
    const positionEntries = matchEntries.filter((entry) => entry.league_configuration_position_id === position.id);
    if (positionEntries.length < requiredPlayerCount(position)) {
      return {
        show: true,
        label: 'EDR Δ —',
        tooltip: 'Incomplete lineup',
        tone: 'unavailable',
        difference: null,
      };
    }

    for (const entry of positionEntries.slice(0, requiredPlayerCount(position))) {
      const player = playersById.get(entry.player_id);
      if (!player || numericValue(player.estimated_dynamic_rating) === null) {
        return {
          show: true,
          label: 'EDR Δ —',
          tooltip: 'Missing player EDR',
          tone: 'unavailable',
          difference: null,
        };
      }
      assignedLineupPlayers.push(player);
    }
  }

  const plannedLineupAverageEdr = calculateLineupAverageEdr(assignedLineupPlayers);
  const difference = calculateEdrDifference(plannedLineupAverageEdr, opponentAverageEdr);
  if (difference === null) {
    return {
      show: true,
      label: 'EDR Δ —',
      tooltip: 'Missing player EDR',
      tone: 'unavailable',
      difference: null,
    };
  }

  const roundedDifference = displayDifference(difference);

  return {
    show: true,
    label: `EDR Δ ${formatEdrDifference(roundedDifference)}`,
    tooltip: 'Planned lineup EDR compared with opponent average EDR',
    tone: edrTone(roundedDifference),
    difference: roundedDifference,
  };
}
